/**
 * Eager bits of the Rush for the map rail: the stopwatch mark (inline SVG until the atlas has
 * an `ico_rush`), the badge dot, the "results card due" check and the "standing is old" check
 * the map hook uses during the weekend. Synchronous, no requests.
 */
import type { JSX } from 'preact';
import type { AppContext } from '../../game/context.js';
import { lastStanding, serverNow, weekendSeen } from './local.js';
import { weekendOf } from './time.js';

type Ctx = Pick<AppContext, 'services'>;

const INK = '#4a2c2a';

/**
 * The API ranks the groups and pays the top 3 in its `tournament.close` job (Monday 00:05 UTC):
 * the results card waits this long after the close so the closing message can be read.
 */
export const RESULT_GRACE_MS = 60 * 60_000;
/**
 * A player last seen in the top 3 waits this long past the close for the closing message (the
 * API's close job runs once a week: when it runs late, the place must not be lost to a card
 * that says there is none).
 */
export const PODIUM_WAIT_MS = 24 * 60 * 60_000;
/** During the weekend the map refreshes a joined player's standing at most this often. */
export const REFRESH_MS = 60 * 60_000;

/** Rush stopwatch with a lightning bolt, in the overlay's toon icon style. */
export function RushMark({ size = 26 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect x="13" y="2" width="6" height="4" rx="1.5" fill="#ff6b5b" stroke={INK} stroke-width="2" />
      <path d="M24.5 7.5l2.5-2.5" stroke={INK} stroke-width="2.4" stroke-linecap="round" />
      <circle cx="16" cy="18" r="12" fill="#ffc845" stroke={INK} stroke-width="2" />
      <circle cx="16" cy="18" r="8.6" fill="#fff8ec" stroke={INK} stroke-width="1.6" />
      <path d="M17.5 11.5l-5 7.5h4l-2 6 5.6-8h-4.1z" fill="#ff6b5b" stroke={INK} stroke-width="1.4" stroke-linejoin="round" />
      <ellipse cx="10" cy="12" rx="2" ry="1.2" fill="#fff" opacity=".6" />
    </svg>
  );
}

/** The last Rush the player played has closed (and been ranked) and its results card was not shown yet. */
export function resultDue(a: Ctx): boolean {
  const s = lastStanding(a);
  return !!s && !s.shown && s.total > 0 && s.closesAt + RESULT_GRACE_MS <= serverNow(a);
}

/** The player is in the running Rush and their standing was last fetched an hour ago or more. */
export function standingStale(a: Ctx): boolean {
  const s = lastStanding(a);
  if (!s || s.closesAt <= serverNow(a)) return false;
  const now = a.services.now();
  return now - s.fetchedAt >= REFRESH_MS || now < s.fetchedAt;
}

/** Dot: results waiting, or a Rush is running that the player has neither joined nor looked at. */
export function railBadge(a: Ctx): true | null {
  if (resultDue(a)) return true;
  const w = weekendOf(serverNow(a));
  if (!w) return null;
  return lastStanding(a)?.id === w.id || weekendSeen(a, w.id) ? null : true;
}
